import React, { useState } from 'react';
import { BaseConfigProps, commonStyles } from '../shared/ConfigFormHelpers';
import CollapsibleSection from '../shared/CollapsibleSection';

const DataValidatorConfig: React.FC<BaseConfigProps> = ({ node, updateConfig }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['rules', 'settings']));
  const [rules, setRules] = useState<Array<{field: string, type: string, required: boolean}>>(
    node.data.config?.rules || []
  );
  
  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const addRule = () => {
    const newRules = [...rules, { field: '', type: 'string', required: false }];
    setRules(newRules);
    updateConfig('rules', newRules);
  };

  const updateRule = (index: number, key: string, value: any) => {
    const newRules = [...rules];
    newRules[index] = { ...newRules[index], [key]: value };
    setRules(newRules);
    updateConfig('rules', newRules);
  };

  const removeRule = (index: number) => {
    const newRules = rules.filter((_, i) => i !== index);
    setRules(newRules);
    updateConfig('rules', newRules);
  };

  const validationTypes = [
    { value: 'string', label: 'Szöveg', icon: '📝' },
    { value: 'number', label: 'Szám', icon: '🔢' },
    { value: 'email', label: 'Email', icon: '📧' },
    { value: 'url', label: 'URL', icon: '🔗' },
    { value: 'date', label: 'Dátum', icon: '📅' },
    { value: 'boolean', label: 'Boolean', icon: '✓' },
    { value: 'array', label: 'Tömb', icon: '📋' },
    { value: 'object', label: 'Objektum', icon: '📦' },
  ];

  return (
    <div className="space-y-3">
      {/* Validációs szabályok */}
      <CollapsibleSection
        id="rules"
        title="Validációs Szabályok"
        icon="✅"
        color="emerald"
        isExpanded={expandedSections.has('rules')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          {rules.length === 0 ? (
            <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
              Még nincsenek szabályok. Adj hozzá egyet!
            </div>
          ) : (
            <div className="space-y-2">
              {rules.map((rule, index) => (
                <div key={index} className="bg-gray-50 dark:bg-gray-800 p-3 rounded-md border border-gray-200 dark:border-gray-700">
                  <div className="grid grid-cols-12 gap-2 items-start">
                    <div className="col-span-5">
                      <input
                        type="text"
                        value={rule.field}
                        onChange={(e) => updateRule(index, 'field', e.target.value)}
                        className={`${commonStyles.input} text-xs`}
                        placeholder="Mező neve"
                      />
                    </div>
                    <div className="col-span-4">
                      <select
                        value={rule.type}
                        onChange={(e) => updateRule(index, 'type', e.target.value)}
                        className={`${commonStyles.select} text-xs`}
                      >
                        {validationTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.icon} {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-2 flex items-center justify-center">
                      <input
                        type="checkbox"
                        checked={rule.required}
                        onChange={(e) => updateRule(index, 'required', e.target.checked)}
                        className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                        title="Kötelező"
                      />
                    </div>
                    <div className="col-span-1 flex items-center justify-center">
                      <button
                        onClick={() => removeRule(index)}
                        className="text-red-500 hover:text-red-700"
                        title="Törlés"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={addRule}
            className="w-full py-2 px-3 border border-dashed border-emerald-300 dark:border-emerald-700 rounded-md text-sm text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
          >
            + Új szabály hozzáadása
          </button>
        </div>
      </CollapsibleSection>

      {/* Beállítások */}
      <CollapsibleSection
        id="settings"
        title="Validáció Beállítások"
        icon="⚙️"
        color="blue"
        isExpanded={expandedSections.has('settings')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div>
            <label className={commonStyles.label}>
              Validálandó adat
            </label>
            <textarea
              value={node.data.config.data || ''}
              onChange={(e) => updateConfig('data', e.target.value)}
              className={commonStyles.textarea}
              rows={4}
              placeholder='{{data}} vagy {"name": "John", "email": "john@example.com"}'
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              JSON objektum vagy változó
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="stopOnFirstError"
              checked={node.data.config.stopOnFirstError || false}
              onChange={(e) => updateConfig('stopOnFirstError', e.target.checked)}
              className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
            />
            <label htmlFor="stopOnFirstError" className="text-sm text-gray-700 dark:text-gray-300">
              Megállás első hibánál
            </label>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
            <div className="text-xs text-blue-700 dark:text-blue-300">
              <strong>💡 Kimenet:</strong> A validátor két output-tal rendelkezik: <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">valid</code> (érvényes adatok) és <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">invalid</code> (hibás adatok).
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Kimenet Változók */}
      <CollapsibleSection
        id="variables"
        title="Kimenet Változók"
        icon="🔗"
        color="purple"
        isExpanded={expandedSections.has('variables')}
        onToggle={toggleSection}
      >
        <div className="space-y-2">
          <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            A validáció után ezek a változók lesznek elérhetők:
          </div>
          
          <div className="grid grid-cols-1 gap-2">
            {[
              { var: '{{valid}}', desc: 'Érvényes adatok', example: '{...}', output: 'valid' },
              { var: '{{invalid}}', desc: 'Hibás adatok', example: '{...}', output: 'invalid' },
              { var: '{{errors}}', desc: 'Hibák listája', example: '[...]', output: 'invalid' },
              { var: '{{isValid}}', desc: 'Érvényes-e', example: 'true/false', output: 'both' }
            ].map((item) => (
              <div key={item.var} className="bg-gray-50 dark:bg-gray-800 rounded-md p-2 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <code className="text-xs font-mono text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-2 py-1 rounded">
                    {item.var}
                  </code>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                    {item.example}
                  </span>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {item.desc} {item.output !== 'both' && `(${item.output} output)`}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CollapsibleSection>

      {/* Példák */}
      <CollapsibleSection
        id="examples"
        title="Használati Példák"
        icon="💡"
        color="amber"
        isExpanded={expandedSections.has('examples')}
        onToggle={toggleSection}
      >
        <div className="space-y-2 text-xs text-gray-600 dark:text-gray-400">
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>✅ Űrlap validáció:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Űrlap → Validátor → [valid] → CRM Insert
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>📧 Email ellenőrzés:</strong>
            <div className="mt-1 font-mono text-[10px]">
              HTTP Request → Validátor → [valid] → Email küldés
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>❌ Hibakezelés:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Validátor → [invalid] → Hibaüzenet → Notification
            </div>
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
};

export default DataValidatorConfig;
